<script lang="ts">
  import { formatDistance } from "$lib/helpers/format-distance";
  import { locationStore } from "$lib/stores/location.svelte";
  import type { PoiItem } from "$lib/types/index";
  import { Hexagon, MapPin } from "lucide-svelte";

  interface Props {
    items: PoiItem[];
    isLoading: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
    onItemClick: (item: PoiItem) => void;
  }

  let { items, isLoading, hasMore, onLoadMore, onItemClick }: Props = $props();
</script>

<div class="relative rounded-none overflow-hidden">
  <div class="absolute inset-0 opacity-10 pointer-events-none"></div>

  {#if items.length === 0 && !isLoading}
    <div class="relative text-center py-16 px-8">
      <div class="inline-block mb-4">
        <MapPin class="w-12 h-12 text-muted-foreground" />
      </div>
      <p class="text-foreground text-lg font-bold">[ Nincs találat ]</p>
    </div>
  {:else}
    <div class="relative space-y-2 pt-1 px-4">
      {#each items as item (item.id)}
        {@const distance = locationStore.userLocation
          ? formatDistance(
              locationStore.calculateDistance(
                {
                  lat: item.latitude,
                  lng: item.longitude,
                },
                locationStore.userLocation
              )
            )
          : null}
        <button
          class="w-full text-left border-2 border-foreground rounded-md bg-white shadow-black hover:shadow-[2px_2px_0px_0px] hover:shadow-foreground p-3 hover:-translate-y-0.5 hover:bg-secondary/70 transition-all group"
          onclick={() => onItemClick(item)}
        >
          <div class="flex gap-4">
            <!-- Image on the left -->
            <div class="flex items-center">
              <img
                alt={item.subCategory}
                src={`/assets/icons/${item.subCategory.toLowerCase()}.svg`}
                class="w-16 h-16 object-contain aspect-square"
              />
            </div>

            <!-- Content area -->
            <div class="flex-1 min-w-0">
              <!-- Big title on top -->
              <div class="flex items-center gap-2 mb-2">
                <h3 class="text-lg font-extrabold text-foreground truncate">
                  {item.name}
                </h3>
                {#if item.hasCoupon}
                  <span
                    class="text-sm text-muted-foreground font-bold whitespace-nowrap"
                  >
                    <img
                      src="/assets/icons/coupon.svg"
                      class="inline h-6"
                      alt="kupon"
                    /> Kupon
                  </span>
                {/if}
              </div>

              <!-- Description under title -->
              {#if item.mainService}
                <p class="text-sm mb-2 line-clamp-2">
                  {item.mainService}
                </p>
              {/if}

              <!-- Tags under description -->
              <div class="flex items-center gap-2 flex-wrap text-xs mb-2">
                {#if item.tags && item.tags.length > 0}
                  {#each item.tags.slice(0, 2) as tag}
                    <span class="text-accent-foreground">
                      #{tag}
                    </span>
                  {/each}
                  {#if item.tags.length > 2}
                    <span class="text-muted-foreground italic">
                      ...+{item.tags.length - 2}
                    </span>
                  {/if}
                {/if}
              </div>

              <!-- Coordinates on bottom left -->
              <div class="flex relative justify-between items-end">
                {#if item.address}
                  <div class="flex items-center gap-1 text-muted-foreground">
                    <span class="text-sm">📍</span>
                    <span class="text-sm truncate">{item.address}</span>
                  </div>
                {/if}

                {#if distance}
                  <div
                    class="absolute right-0 text-xs font-mono text-muted-foreground bg-input px-2 py-1 border border-foreground"
                  >
                    {distance}
                  </div>
                {/if}
              </div>
            </div>
          </div>
        </button>
      {/each}
    </div>

    {#if hasMore}
      <div class="relative border-t-2 border-border pt-4 pb-2 px-4">
        <button
          class="w-full py-3 px-6 text-foreground font-bold border-2 border-foreground
                 hover:bg-accent/10 disabled:opacity-50 disabled:cursor-not-allowed
                 transition-all hover:shadow-[2px_2px_0px_0px] hover:shadow-foreground hover:-translate-y-0.5
                 active:translate-x-0.5 active:translate-y-0.5 active:shadow-none"
          onclick={onLoadMore}
          disabled={isLoading}
        >
          {#if isLoading}
            <span class="inline-flex items-center gap-2">
              <div class="relative animate-spin-slow opacity-60">
                <Hexagon
                  class="w-32 h-32 text-black fill-primary"
                  strokeWidth={2.5}
                />
                <div class="absolute inset-0 flex items-center justify-center">
                  <img src="/assets/ui-icons/beeco-logo.svg" alt="Logo" />
                </div>
              </div>
              [ Betöltés... ]
            </span>
          {:else}
            [ → További eredmények ]
          {/if}
        </button>
      </div>
    {/if}
  {/if}
</div>

<style>
  @import url("https://fonts.googleapis.com/css2?family=Kalam:wght@300;400;700&display=swap");

  .font-mono {
    font-family: "Kalam", cursive, monospace;
  }

  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
</style>
